import { Router } from "express";
import { AvailabilityController } from "../controllers/availability.controller";
import { Routes } from "../../../interface/routes.interface";
import { permissionInjector } from "../../../middlewares/permissionInjector";

/**
 * @swagger
 * tags:
 *   name: Availability
 *   description: Availability Management APIs
 */

export class AvailabilityRouter implements Routes {
    public router = Router();
    public availabilityController = new AvailabilityController();
    public path = '/availability';

    constructor() {
        this.initializeRoutes();
    }

    public initializeRoutes() {

        /**
         * @swagger
         * /availability/calendar:
         *   get:
         *     summary: Get calendar data for availabilities and allocation requests
         *     tags: [Availability]
         *     security:
         *       - bearerAuth: []
         *     parameters:
         *       - in: query
         *         name: month
         *         schema:
         *           type: integer
         *           minimum: 1
         *           maximum: 12
         *         description: Month for which to retrieve data
         *       - in: query
         *         name: year
         *         schema:
         *           type: integer
         *           minimum: 1970
         *         description: Year for which to retrieve data
         *       - in: query
         *         name: date
         *         schema:
         *           type: string
         *           format: date
         *         description: Specific date to filter data (YYYY-MM-DD format, optional)
         *         example: 2025-07-01
         *       - in: query
         *         name: availableStatus
         *         schema:
         *           type: string
         *           enum: [AVAILABLE, UNAVAILABLE]
         *         description: Filter by availability status (optional)
         *         example: AVAILABLE
         *     responses:
         *       200:
         *         description: Calendar data retrieved
         *         content:
         *           application/json:
         *             schema:
         *               type: object
         *               properties:
         *                 status:
         *                   type: string
         *                   example: OK
         *                 message:
         *                   type: string
         *                 data:
         *                   type: array
         *                   items:
         *                     type: object
         *                     properties:
         *                       _id:
         *                         type: string
         *                         description: Unique identifier for the allocation request
         *                         example: 685a7043fab4351960176aec
         *                       requestedDte:
         *                         type: string
         *                         format: date-time
         *                         description: Date when the request was made
         *                         example: 2025-06-24T09:30:43.761Z
         *                       companyName:
         *                         type: string
         *                         description: Name of the company
         *                         example: Gard-Labour-Hire
         *                       startDate:
         *                         type: string
         *                         format: date-time
         *                         description: Start date of the allocation period
         *                         example: 2025-07-11T00:00:00.000Z
         *                       endDate:
         *                         type: string
         *                         format: date-time
         *                         description: End date of the allocation period
         *                         example: 2025-07-12T00:00:00.000Z
         *                       wages:
         *                         type: number
         *                         description: Wage offered
         *                         example: 30
         *                       location:
         *                         type: string
         *                         description: Location of the allocation
         *                         example: Sydney, NSW
         *                       email:
         *                         type: string
         *                         format: email
         *                         description: Email of the user
         *                         example: labour3@gmail.com
         *                       firstName:
         *                         type: string
         *                         description: First name of the user
         *                         example: Updated
         *                       lastName:
         *                         type: string
         *                         description: Last name of the user
         *                         example: Labour
         *                       status:
         *                         type: string
         *                         description: Status of the allocation request
         *                         example: pending
         *                       phone:
         *                         type: string
         *                         description: Phone number of the user
         *                         example: 7047858741
         *                 total:
         *                   type: integer
         *                   description: Total number of records
         *                   example: 3
         *                 page:
         *                   type: integer
         *                   description: Current page number
         *                   example: 1
         *                 totalPage:
         *                   type: integer
         *                   description: Total number of pages
         *                   example: 1
         *                 count:
         *                   type: object
         *                   properties:
         *                     pending:
         *                       type: integer
         *                       description: Number of pending requests
         *                       example: 2
         *                     accepted:
         *                       type: integer
         *                       description: Number of accepted requests
         *                       example: 0
         *                     rejected:
         *                       type: integer
         *                       description: Number of rejected requests
         *                       example: 1
         *       400:
         *         description: Bad request
         *       401:
         *         description: Unauthorized
         *       500:
         *         description: Internal server error
         */
        this.router.get(`${this.path}/calendar`, permissionInjector(`${this.path}/calendar`, 'GET'), this.availabilityController.getCalendarData);

        /**
         * @swagger
         * /availability:
         *   post:
         *     summary: Set availability
         *     tags: [Availability]
         *     security:
         *       - bearerAuth: []
         *     requestBody:
         *       required: true
         *       content:
         *         application/json:
         *           schema:
         *             type: object
         *             properties:
         *               userId:
         *                 type: string
         *                 description: ID of the user (labourer) for the availability
         *               startDate:
         *                 type: string
         *                 format: date-time
         *                 description: Start date of the availability period
         *               endDate:
         *                 type: string
         *                 format: date-time
         *                 description: End date of the availability period
         *               jobId:
         *                 type: string
         *                 description: job Id
         *               status:
         *                 type: string
         *                 enum: [HOLIDAY, UNAVAILABLE]
         *                 description: Availability status
         *             required:
         *               - userId
         *               - startDate
         *               - endDate
         *               - status
         *     responses:
         *       201:
         *         description: Unavailability created successfully
         *         content:
         *           application/json:
         *             schema:
         *               type: object
         *               properties:
         *                 status:
         *                   type: string
         *                   example: Created
         *                 message:
         *                   type: string
         *                 data:
         *                   $ref: '#/components/schemas/Availability'
         *       400:
         *         description: Bad request
         *       401:
         *         description: Unauthorized
         *       500:
         *         description: Internal server error
         */
        this.router.post(`${this.path}`, permissionInjector(`${this.path}`, 'POST'), this.availabilityController.setAvailability);

        /**
         * @swagger
         * /availability/getAll:
         *   get:
         *     summary: Get all availabilities
         *     tags: [Availability]
         *     security:
         *       - bearerAuth: []
         *     parameters:
         *       - in: query
         *         name: page
         *         schema:
         *           type: integer
         *           default: 1
         *         description: Page number for pagination
         *       - in: query
         *         name: limit
         *         schema:
         *           type: integer
         *           default: 10
         *         description: Number of records per page
         *       - in: query
         *         name: startDate
         *         schema:
         *           type: string
         *         description: Start date of the availability
         *         example: 2025-07-29
         *       - in: query
         *         name: endDate
         *         schema:
         *           type: string
         *         description: End date of the availability
         *         example: 2025-12-29
         *       - in: query
         *         name: isPartial
         *         schema:
         *           type: string
         *           enum: [true, false]
         *           default: false
         *         description: Include partial availability within the date range if true
         *       - in: query
         *         name: availability_status
         *         schema:
         *           type: string
         *         description: Filter by labourer availability status (case-insensitive)
         *       - in: query
         *         name: occupation
         *         schema:
         *           type: array
         *           items:
         *             type: string
         *         description: Filter by labourer occupations (can be a single value or multiple values)
         *         example: ["Electrician", "Carpenter"]
         *       - in: query
         *         name: tickets
         *         schema:
         *           type: array
         *           items:
         *             type: string
         *         description: Filter by labourer tickets (can be a single value or multiple values)
         *         example: ["White Card"]
         *       - in: query
         *         name: sort
         *         schema:
         *           type: string
         *         description: JSON string specifying sort criteria
         *       - in: query
         *         name: search
         *         schema:
         *           type: string
         *         description: search name 
         *     responses:
         *       200:
         *         description: List of availabilities
         *         content:
         *           application/json:
         *             schema:
         *               type: object
         *               properties:
         *                 status:
         *                   type: string
         *                   example: OK
         *                 message:
         *                   type: string
         *                 data:
         *                   type: array
         *                   items:
         *                     type: object
         *                     properties:
         *                       _id:
         *                         type: string
         *                         description: Unique identifier for the availability
         *                         example: 6851135b697781e972b5e57a
         *                       userId:
         *                         type: string
         *                         description: ID of the user (labourer)
         *                         example: 68511358697781e972b5e573
         *                       firstName:
         *                         type: string
         *                         description: First name of the user
         *                         example: Updated
         *                       lastName:
         *                         type: string
         *                         description: Last name of the user
         *                         example: Labour
         *                       email:
         *                         type: string
         *                         format: email
         *                         description: Email of the user
         *                         example: labour@example.com
         *                       employee_id:
         *                         type: string
         *                         description: Employee ID of the labourer
         *                         example: LBR-00001
         *                       skills:
         *                         type: array
         *                         items:
         *                           type: string
         *                         description: Skills of the labourer
         *                         example: ["Electrician", "Carpenter"]
         *                       availability_status:
         *                         type: string
         *                         description: Status of availability
         *                         example: Available
         *                       startDate:
         *                         type: string
         *                         format: date-time
         *                         description: Start date of the availability period
         *                         example: 2025-01-31T00:00:00.000Z
         *                       endDate:
         *                         type: string
         *                         format: date-time
         *                         description: End date of the availability period
         *                         example: 2025-02-01T23:59:59.000Z
         *                       isPartial:
         *                         type: boolean
         *                         description: Indicates if the availability is partial
         *                         example: false
         *                 total:
         *                   type: integer
         *                   description: Total number of records
         *                   example: 5
         *                 page:
         *                   type: integer
         *                   description: Current page number
         *                   example: 1
         *                 totalPage:
         *                   type: integer
         *                   description: Total number of pages
         *                   example: 1
         *       401:
         *         description: Unauthorized
         *       500:
         *         description: Internal server error
         */
        this.router.get(`${this.path}/getAll`, permissionInjector(`${this.path}`, 'GET'), this.availabilityController.getAllAvailabilities);

        /**
         * @swagger
         * /availability/{id}:
         *   get:
         *     summary: Get availability by ID
         *     tags: [Availability]
         *     security:
         *       - bearerAuth: []
         *     parameters:
         *       - in: path
         *         name: id
         *         required: true
         *         schema:
         *           type: string
         *         description: ID of the availability
         *     responses:
         *       200:
         *         description: Availability found
         *         content:
         *           application/json:
         *             schema:
         *               type: object
         *               properties:
         *                 status:
         *                   type: string
         *                   example: OK
         *                 message:
         *                   type: string
         *                 data:
         *                   $ref: '#/components/schemas/Availability'
         *       404:
         *         description: Availability not found
         *       401:
         *         description: Unauthorized
         *       500:
         *         description: Internal server error
         */
        this.router.get(`${this.path}/:id`, permissionInjector(`${this.path}`, 'GET'), this.availabilityController.getAvailabilityById);

        /**
         * @swagger
         * /availability/{id}:
         *   put:
         *     summary: Update an availability
         *     tags: [Availability]
         *     security:
         *       - bearerAuth: []
         *     parameters:
         *       - in: path
         *         name: id
         *         required: true
         *         schema:
         *           type: string
         *         description: ID of the availability
         *     requestBody:
         *       required: true
         *       content:
         *         application/json:
         *           schema:
         *             type: object
         *             properties:
         *               userId:
         *                 type: string
         *                 description: ID of the user (labourer)
         *               startDate:
         *                 type: string
         *                 format: date-time
         *                 description: Start date of the availability period
         *               endDate:
         *                 type: string
         *                 format: date-time
         *                 description: End date of the availability period
         *               companyName:
         *                 type: string
         *                 description: Name of the company
         *               location:
         *                 type: string
         *                 description: Location of the availability
         *               status:
         *                 type: string
         *                 enum: [AVAILABLE, UNAVAILABLE]
         *                 description: Availability status
         *     responses:
         *       200:
         *         description: Availability updated
         *         content:
         *           application/json:
         *             schema:
         *               type: object
         *               properties:
         *                 status:
         *                   type: string
         *                   example: OK
         *                 message:
         *                   type: string
         *                 data:
         *                   $ref: '#/components/schemas/Availability'
         *       400:
         *         description: Bad request
         *       401:
         *         description: Unauthorized
         *       404:
         *         description: Availability not found
         *       500:
         *         description: Internal server error
         */
        this.router.put(`${this.path}/:id`, permissionInjector(`${this.path}`, 'PUT'), this.availabilityController.updateAvailability);

        /**
         * @swagger
         * /availability:
         *   delete:
         *     summary: Delete an availability
         *     tags: [Availability]
         *     security:
         *       - bearerAuth: []
         *     requestBody:
         *       required: true
         *       content:
         *         application/json:
         *           schema:
         *             type: object
         *             required: [ids]
         *             properties:
         *               ids:
         *                 type: array
         *                 example: ["ids"]
         *     responses:
         *       200:
         *         description: Availability deleted
         *         content:
         *           application/json:
         *             schema:
         *               type: object
         *               properties:
         *                 status:
         *                   type: string
         *                   example: OK
         *                 message:
         *                   type: string
         *                 data:
         *                   type: boolean
         *       404:
         *         description: Availability not found
         *       401:
         *         description: Unauthorized
         *       500:
         *         description: Internal server error
         */
        this.router.delete(`${this.path}`, permissionInjector(`${this.path}`, 'DELETE'), this.availabilityController.deleteAvailability);

        /**
         * @swagger
         * /availability/userId/calendar:
         *   get:
         *     summary: Get User availabilities
         *     tags: [Availability]
         *     security:
         *       - bearerAuth: []
         *     parameters:
         *       - in: query
         *         name: startDate
         *         schema:
         *           type: string
         *         description: Start date of the availability
         *         example: 2025-07-29
         *       - in: query
         *         name: endDate
         *         schema:
         *           type: string
         *         description: End date of the availability
         *         example: 2025-12-29
         *       - in: query
         *         name: availableStatus
         *         schema:
         *           type: string
         *         description: Filter by labourer availability status (case-insensitive)
         *     responses:
         *       200:
         *         description: List of availabilities
         *         content:
         *           application/json:
         *             schema:
         *               type: object
         *               properties:
         *                 status:
         *                   type: string
         *                   example: OK
         *                 message:
         *                   type: string
         *                 data:
         *                   type: array
         *                   items:
         *                     type: object
         *                     properties:
         *                       _id:
         *                         type: string
         *                         description: Unique identifier for the availability
         *                         example: 6851135b697781e972b5e57a
         *                       userId:
         *                         type: string
         *                         description: ID of the user (labourer)
         *                         example: 68511358697781e972b5e573
         *                       firstName:
         *                         type: string
         *                         description: First name of the user
         *                         example: Updated
         *                       lastName:
         *                         type: string
         *                         description: Last name of the user
         *                         example: Labour
         *                       email:
         *                         type: string
         *                         format: email
         *                         description: Email of the user
         *                         example: labour@example.com
         *                       employee_id:
         *                         type: string
         *                         description: Employee ID of the labourer
         *                         example: LBR-00001
         *                       skills:
         *                         type: array
         *                         items:
         *                           type: string
         *                         description: Skills of the labourer
         *                         example: ["Electrician", "Carpenter"]
         *                       availability_status:
         *                         type: string
         *                         description: Status of availability
         *                         example: Available
         *                       startDate:
         *                         type: string
         *                         format: date-time
         *                         description: Start date of the availability period
         *                         example: 2025-01-31T00:00:00.000Z
         *                       endDate:
         *                         type: string
         *                         format: date-time
         *                         description: End date of the availability period
         *                         example: 2025-02-01T23:59:59.000Z
         *                 total:
         *                   type: integer
         *                   description: Total number of records
         *                   example: 5
         *                 page:
         *                   type: integer
         *                   description: Current page number
         *                   example: 1
         *                 totalPage:
         *                   type: integer
         *                   description: Total number of pages
         *                   example: 1
         *       401:
         *         description: Unauthorized
         *       500:
         *         description: Internal server error
         */

        this.router.get(`${this.path}/userId/calendar`, permissionInjector(`${this.path}`, 'GET'), this.availabilityController.getUserCalendarData)
    }
}

/**
 * @swagger
 * components:
 *   schemas:
 *     Availability:
 *       type: object
 *       properties:
 *         _id:
 *           type: string
 *           description: Unique identifier for the availability
 *           example: 60f6c0f2e13e3c1e4c8e3a6b
 *         userId:
 *           type: string
 *           description: Unique identifier for the user
 *           example: 507f1f77bcf86cd799439011
 *         firstName:
 *           type: string
 *           description: First name of the user
 *           example: John
 *         lastName:
 *           type: string
 *           description: Last name of the user
 *           example: Doe
 *         email:
 *           type: string
 *           format: email
 *           description: Email address of the user
 *           example: john.doe@example.com
 *         employee_id:
 *           type: string
 *           description: Employee ID of the labourer
 *           example: LBR-00001
 *         skills:
 *           type: array
 *           items:
 *             type: string
 *           description: Skills of the labourer
 *           example: ["Electrician", "Carpenter"]
 *         availability_status:
 *           type: string
 *           description: Status of availability
 *           example: Available
 *         startDate:
 *           type: string
 *           format: date-time
 *           description: Start date of the availability period
 *           example: 2025-07-01T09:00:00Z
 *         endDate:
 *           type: string
 *           format: date-time
 *           description: End date of the availability period
 *           example: 2025-07-05T18:00:00Z
 *         companyName:
 *           type: string
 *           description: Name of the company (optional)
 *           example: ABC Constructions
 *         location:
 *           type: string
 *           description: Location of the availability
 *           example: Mumbai
 *         createdBy:
 *           type: string
 *           description: Unique identifier of the user who created the availability
 *           example: 507f1f77bcf86cd799439021
 *         updatedBy:
 *           type: string
 *           description: Unique identifier of the user who last updated the availability
 *           example: 507f1f77bcf86cd799439022
 *       required:
 *         - userId
 *         - startDate
 *         - endDate
 *         - createdBy
 *     CalendarEvent:
 *       type: object
 *       properties:
 *         id:
 *           type: string
 *           description: Unique identifier of the event (either availability or allocation request)
 *           example: 60f6c0f2e13e3c1e4c8e3a6b
 *         title:
 *           type: string
 *           description: Title describing the event with user name and status
 *           example: John Doe - UNAVAILABLE
 *         start:
 *           type: string
 *           format: date-time
 *           description: Start date of the event
 *           example: 2025-07-01T09:00:00Z
 *         end:
 *           type: string
 *           format: date-time
 *           description: End date of the event
 *           example: 2025-07-05T18:00:00Z
 *         extendedProps:
 *           type: object
 *           properties:
 *             type:
 *               type: string
 *               enum: [availability, allocation]
 *               description: Type of event (availability or allocation)
 *               example: availability
 *             status:
 *               type: string
 *               description: Status of the event (e.g., AVAILABLE, UNAVAILABLE, PENDING, ACCEPTED, REJECTED)
 *               example: UNAVAILABLE
 *             location:
 *               type: string
 *               description: Location of the event
 *               example: Surat
 *             companyName:
 *               type: string
 *               description: Company name for availability event (optional)
 *               example: Shree Construction
 *             wage:
 *               type: number
 *               format: float
 *               description: Wage for allocation request event (only applicable if type is 'allocation')
 *               example: 750
 *             message:
 *               type: string
 *               description: Message for allocation request (only applicable if type is 'allocation')
 *               example: Please join for project site A for 2 days.
 */